import {
  Component,
  Element,
  h,
  Host,
  Prop,
  Event,
  Watch,
  State,
  Listen,
  Fragment,
  EventEmitter
} from '@stencil/core';

import * as d3 from 'd3';
import { select } from 'd3-selection';
import {
  CHART_TYPE,
  STACKED_BAR_CHART_TOOLTIP_BEHAVIOR
} from '../../constants';
import { getChartModel } from '../../utils/model-helper';
import {
  IBarChart,
  IChartData,
  IGroupBarTooltipDataItem,
  ILineChart,
  IStackedBar,
  LineChart,
  StackedBarChart
} from '../../models';
import { getFormattedNumberAbbrev } from '../../utils';

@Component({
  tag: 'uui-stacked-bar-chart',
  styleUrl: 'uui-stacked-bar-chart.scss',
  shadow: true
})
export class UuiStackedBarChart {
  @Element() element: HTMLElement;
  @Event({ bubbles: true, composed: true }) legendClick: EventEmitter;
  @Prop() config: IStackedBar;
  @Prop() trendLineConfig?: ILineChart;
  @State() width: number;
  @State() highlightedGroup: string = '';

  //Todo: Find approprate d3 data types
  public chart: any;
  public barsSvgElement: any;

  noBenchmarkToolTipElement: HTMLElement;
  model: StackedBarChart;
  trendLineModel: LineChart;
  plotAreaWidth: number;
  toolTipElement: HTMLElement;
  highlightedBarData: IChartData;
  hasEmptyMessageSlot: boolean;
  verticalDataKeyVsDataAccumulatedValuePair = new Map<string, number>();
  horizontalDataKeyVsDataAccumulatedValuePair = new Map<string, number>();

  @Listen('resize', { target: 'window' })
  windowResize() {
    if (this.model) {
      this.createChart();
    }

    if (this.trendLineModel) {
      this.createLineChart();
    }
  }

  @Watch('config')
  onConfigChanged(): void {
    this.buildModel();
    this.createChart();

    if (this.trendLineModel) {
      this.createLineChart();
    }
  }

  componentWillLoad(): void {
    this.hasEmptyMessageSlot = !!this.element.querySelector(
      '[slot="empty-message"]'
    );
    this.buildModel();
  }

  componentDidLoad(): void {
    this.createChart();

    if (this.trendLineModel) {
      this.createLineChart();
    }
  }

  buildModel(): void {
    this.model = getChartModel<CHART_TYPE.STACKED_BAR>(
      this.config as IBarChart
    );

    if (this.trendLineConfig) {
      this.trendLineModel = getChartModel<CHART_TYPE.LINE>({
        ...(this.trendLineConfig as ILineChart),
        width: this.model.width,
        height: this.model.height
      });
    }
  }

  createChart(): void {
    if (!this.model) {
      return;
    }

    if (!this.model.series?.length) {
      return;
    }

    this.width = this.model.stretchToMaxWidth
      ? this.element?.getBoundingClientRect().width
      : this.model.width;

    const svgElement = select(this.element.shadowRoot.querySelector('svg'))
      .attr('width', this.width)
      .attr('height', this.model.height);

    this.plotAreaWidth = this.model.stretchToMaxWidth
      ? this.width * this.model.widthModifier
      : this.model.plotAreaWidth;

    this.model.plotAreaWidth = this.plotAreaWidth;

    svgElement.selectAll('*').remove();

    this.chart = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    // Y Axis Title
    if (this.model.yAxis.showAxisLabel) {
      this.chart
        .append('text')
        .attr(
          'transform',
          `translate(${this.model.yAxisTitleLeftMargin} ${
            this.model.plotAreaHeight / 2
          }) rotate(-90)`
        )
        .attr('text-anchor', 'middle')
        .text(this.model.yAxis.title)
        .attr('class', 'uui-dv-axis-label');
    }

    // X Axis Title
    const xAxisTitle = this.chart
      .append('text')
      .attr('x', 0)
      .attr('y', this.model.plotAreaHeight + 45) // this calculation should be less than the overall chart height
      .attr('text-anchor', 'start')
      .text(this.model.xAxis.title)
      .attr('class', 'uui-dv-axis-label');

    const yAxisSvg = svgElement
      .append('g')
      .attr('class', 'chart__axis--hidden uui-dv-data-label')
      .attr(
        'transform',
        `translate(${this.model.yAxisSvgLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    const xAxisSvg = svgElement
      .append('g')
      .attr('class', 'chart__axis uui-dv-data-label')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin},
             ${this.model.plotAreaTopMargin + this.model.plotAreaHeight})`
      );

    if (!this.model.xAxis.showAxisLine) {
      xAxisSvg.attr('class', 'chart__axis--hidden uui-dv-data-label');
    }

    if (this.model.isSmallChart) {
      yAxisSvg.call(this.model.leftAxis.tickValues([]));
    }

    yAxisSvg.call(this.model.leftAxis);
    xAxisSvg.call(this.model.bottomAxis);
    if (!this.model.isHorizontal) {
      xAxisSvg
        .selectAll('g.tick')
        .attr('text-anchor', 'end')
        .attr('transform', (d: any) => {
          const xPosition = this.model.xScale(d) + this.model.barWidth / 2;
          return `translate(${xPosition}, 0)`;
        });

      xAxisSvg.selectAll('g.tick text').attr('dy', () => {
        return '0.95em';
      });
    }

    const xAxisWidth =
      (xAxisSvg.node() as SVGElement)?.getBoundingClientRect().width * 0.98;
    const xAxisTitleWidth = xAxisTitle.node()?.getComputedTextLength();

    if (xAxisTitleWidth > xAxisWidth) {
      xAxisTitle
        .attr('x', xAxisTitleWidth / 2)
        .style('max-width', xAxisWidth + 'px')
        .call(this.wrapAndPositionText, xAxisWidth, xAxisWidth / 2);

      this.model.noOfAxisLines = xAxisTitle.selectAll('tspan').size();
      this.model.height = this.model.getCalculatedHeight();
      svgElement.attr('width', this.width).attr('height', this.model.height);
    } else {
      xAxisTitle
        .attr('x', this.plotAreaWidth / 2)
        .attr('text-anchor', 'middle');
    }

    if (this.model.isHorizontal) {
      yAxisSvg
        .call(this.model.leftAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr(
          'transform',
          `translate(80, ${-(this.model.yScale.bandwidth() / 3)})`
        );
    }

    if (this.model.isSmallChart && !this.model.isHorizontal) {
      xAxisSvg
        .call(this.model.bottomAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('transform', `translate(10, 0)`);
    }

    this.barsSvgElement = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    this.updateChart();
  }

  createLineChart(): void {
    if (!this.trendLineModel?.series?.length) {
      return;
    }
    const svg = select(this.element.shadowRoot.querySelector('svg'));

    this.trendLineModel.setPlotAreaWidth(this.width);

    const lineChart: any = svg
      .append('g')
      .attr('class', 'trend-line')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    const line = d3
      .line()
      .curve(d3.curveNatural)
      .x((d: any) => {
        return (
          this.trendLineModel?.getLineOriginCoordinateX(this.model.xScale, d) -
          this.model.xScale.bandwidth() / 2 +
          this.model.barWidth / 3
        );
      })
      .y((d: any) =>
        this.trendLineModel?.getLineOriginCoordinateY(this.model.yScale, d)
      );

    this.trendLineModel.series.forEach(series => {
      const lines = lineChart
        .append('path')
        .datum(series.data)
        .attr('class', 'line')
        .attr('d', line);

      lines
        .attr('stroke', 'var(--uui-color-dataviz-teal)')
        .style('fill', 'none')
        .style('stroke-width', '2px');
    });
  }

  public wrapAndPositionText(text, width, xCoordinate) {
    text.each(function () {
      const text = d3.select(this);
      const words = text.text().split(/\s+/).reverse();
      let word;
      let line = [];
      const lineHeight = 20;
      let tspan = text.text(null).append('tspan').attr('x', xCoordinate);

      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(' '));
        if (tspan.node()?.getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = text
            .append('tspan')
            .attr('x', xCoordinate)
            .attr('dy', lineHeight)
            .text(word);
        }
      }
    });
  }

  emitLabelPayload(event: Event, dataIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    const payload =
      this.model.legend.callbackPayload.length > dataIndex
        ? this.model.legend.callbackPayload[dataIndex]
        : undefined;

    this.legendClick.emit(payload);
  }

  public updateChart(): void {
    // Reset accumulated height for vertical / horizontal bars
    this.horizontalDataKeyVsDataAccumulatedValuePair.clear();
    this.verticalDataKeyVsDataAccumulatedValuePair.clear();

    this.model.series.forEach((series, seriesIndex) => {
      const smallChartDataLabels = this.barsSvgElement
        .selectAll('.bar')
        .data(series.data);

      const bars = this.barsSvgElement
        .selectAll('.bar')
        .data(
          series.data
            .filter(bar => !bar.isHidden)
            .map(data => this._constructStackedChartData(data, series.name))
        );
      bars.exit().remove();

      // bars
      const paths = bars
        .enter()
        .append('rect')
        .style('fill', (d: any) =>
          this.model.getColorForDataNIndex(d, seriesIndex)
        )
        .attr('x', (d: any) => {
          if (this.model.isHorizontal) {
            let kvp =
              this.horizontalDataKeyVsDataAccumulatedValuePair.get(d.name) ?? 0;
            const xCoord = this.model.getBarOriginCoordinateX(
              this.model.xScale,
              d,
              kvp
            );
            this.horizontalDataKeyVsDataAccumulatedValuePair.set(
              d.name,
              kvp + d.y
            );
            return xCoord;
          }

          return this.model.getBarOriginCoordinateX(this.model.xScale, d);
        })
        .attr('y', (d: any) => {
          if (this.model.isHorizontal) {
            return this.model.getBarOriginCoordinateY(
              this.model.yScale,
              d,
              seriesIndex
            );
          }

          let kvp =
            this.verticalDataKeyVsDataAccumulatedValuePair.get(d.name) ?? 0;
          const yCoord = this.model.getBarOriginCoordinateY(
            this.model.yScale,
            d,
            kvp
          );
          this.verticalDataKeyVsDataAccumulatedValuePair.set(d.name, kvp + d.y);
          return yCoord;
        })
        .attr('width', (d: any) => this.model.getBarWidth(this.model.xScale, d))
        .attr('height', (d: any) =>
          this.model.getBarHeight(this.model.yScale, d)
        );

      if (this.model.tooltip) {
        paths
          .on('mouseover', (e: MouseEvent, d: any) => {
            this._mouseOverTooltipHandler(bars, d, e);
            this.highlightedGroup = d.name;
            this.highlightedBarData = d;
            this.toogleTooltip(true);
            this.updateTooltipPosition(e.x, e.y);
          })
          .on('mousemove', (e: MouseEvent) => {
            this.updateTooltipPosition(e.x, e.y);
          })
          .on('mouseout', () => {
            bars.enter().selectAll('rect').attr('opacity', '100%');

            this.highlightedGroup = '';
            this.toogleTooltip(false);
            this.updateTooltipPosition(-1000, -1000); // to displace the tooltip
          });
      }

      if (
        this.model.isSmallChart &&
        this.model.isHorizontal &&
        seriesIndex === 0
      ) {
        const path = smallChartDataLabels
          .enter()
          .append('svg:foreignObject')
          .attr('x', 0)
          .attr(
            'y',
            (d: any) =>
              this.model.getBarOriginCoordinateY(
                this.model.yScale,
                d,
                seriesIndex
              ) - (!d.isHidden ? 20 : 10)
          )
          .attr('width', this.width * 0.9)
          .attr('height', 20);

        var div = path.append('xhtml:div').attr('class', 'uui-dv-legend-label');

        this.getBarDataLabelHtml(div);

        if (this.model.legend && this.model.legend.callbackPayload) {
          path.on('click', (e: any, d: any) => {
            this.emitLabelPayload(e, series.data.indexOf(d));
          });
        }
      }

      if(!this.config.hideDataLabels) {
        bars
        .enter()
        .append('text')
        .attr('text-anchor', 'middle')
        .attr(
          'class',
          `uui-dv-data-label ${
            this.model.isHorizontal ? 'chart-text-label-start' : ''
          }`
        )
        .attr('x', (d: any) => {
          if (!this.model.isTopBar(seriesIndex)) {
            return;
          }

          return this.model.isHorizontal
            ? this.model.getDataLabelCoordinateX(
                this.model.xScale,
                d,
                this.horizontalDataKeyVsDataAccumulatedValuePair.get(d.name)
              )
            : this.model.getDataLabelCoordinateX(this.model.xScale, d);
        })
        .attr('y', (d: any) => {
          if (!this.model.isTopBar(seriesIndex)) {
            return;
          }

          return this.model.isHorizontal
            ? this.model.getDataLabelCoordinateY(
                this.model.yScale,
                d,
                this.horizontalDataKeyVsDataAccumulatedValuePair.get(d.name)
              )
            : this.model.getDataLabelCoordinateY(
                this.model.yScale,
                d,
                this.verticalDataKeyVsDataAccumulatedValuePair.get(d.name)
              );
        })
        .text((d: any) => {
          if (!this.model.isTopBar(seriesIndex)) {
            return;
          }

          return this.model.isHorizontal
            ? `${this.model.valuePrefix || ''}
              ${this.model.getFormattedDataLabel(
                this.horizontalDataKeyVsDataAccumulatedValuePair
                  .get(d.name)
                  .toString(),
                d.displayLabelY,
                this.model.decimalPlaces
              )}
              ${this.model.valueSuffix || ''}`
            : `${this.model.valuePrefix || ''}
              ${this.model.getFormattedDataLabel(
                this.verticalDataKeyVsDataAccumulatedValuePair
                  .get(d.name)
                  .toString(),
                d.displayLabelY,
                this.model.decimalPlaces
              )}
              ${this.model.valueSuffix || ''}`;
        });
      }
     
    });
  }

  private _constructStackedChartData(
    rawChartData: IChartData,
    seriesName: string
  ): IChartData {
    return {
      name: rawChartData.name,
      x: seriesName,
      y: rawChartData.y
    };
  }

  private _mouseOverTooltipHandler(
    bars: any,
    data: IChartData,
    hoveringEvent: MouseEvent
  ): void {
    switch (this.model.tooltipBehavior) {
      case STACKED_BAR_CHART_TOOLTIP_BEHAVIOR.INDIVIDUAL:
        bars.enter().selectAll('rect').attr('opacity', '30%');

        //TODO: Remove tsignore
        // @ts-ignore
        d3.select(hoveringEvent.currentTarget).attr('opacity', '100%');
        break;
      case STACKED_BAR_CHART_TOOLTIP_BEHAVIOR.ISOLATED:
      default:
        bars
          .enter()
          .selectAll('rect')
          .filter(group => group?.name !== data.name)
          .attr('opacity', '30%');
        break;
    }
  }

  getBarDataLabelHtml(div: any): void {
    // data point label
    div.append('span').html((d: any) => d.name);

    // No Benchmark Icon and tooltip
    div
      .append('span')
      .on('mouseover', (e: MouseEvent, d: any) => {
        if (this.model.displayNoBenchmarkIcon(d.name)) {
          this.toggleNoBenchmarkTooltip(true);
          this.updateNoBenchMarkTooltipPosition(e.x, e.y);
        }
      })
      .on('mousemove', (e: MouseEvent, d: any) => {
        if (this.model.displayNoBenchmarkIcon(d.name)) {
          this.updateNoBenchMarkTooltipPosition(e.x, e.y);
        }
      })
      .on('mouseout', () => {
        this.toggleNoBenchmarkTooltip(false);
        this.updateNoBenchMarkTooltipPosition(-1000, -1000); // to displace the tooltip
      })
      .html((d: any) => {
        const displayNoBenchmarkIcon = this.model.displayNoBenchmarkIcon(
          d.name
        );
        return displayNoBenchmarkIcon
          ? ` <uui-icon type="info-circle" color="#b37400" size="sm"</uui-icon>AB`
          : '';
      });

    // data label hyperlink
    div.append('span').html((_d: any, index: any) => {
      const isLinkEnabled =
        this.model.legend?.callbackPayload?.length > index &&
        this.model.legend.callbackPayload[index].enabled;
      return isLinkEnabled
        ? `<span class="chart-link">${this.model.legend.callbackPayload[index].label}</span>`
        : '';
    });
  }

  toggleNoBenchmarkTooltip(isShow: boolean): void {
    this.noBenchmarkToolTipElement.style.opacity = isShow ? '1' : '0';
  }

  getHorizontalSmallChartLabelHtml(labelName, index): string {
    const isLinkEnabled =
      this.model.legend?.callbackPayload?.length > index &&
      this.model.legend.callbackPayload[index].enabled;

    return `<div class="uui-dv-legend-label">
                <span>${labelName}</span>
                ${
                  isLinkEnabled
                    ? `<span class="chart-link">${this.model.legend.callbackPayload[index].label}</span>`
                    : ''
                }
              </div>`;
  }

  getLegendItem(legendName: string, legendIndex: number): JSX.Element {
    return (
      <Fragment>
        <div class="chart__legend--flex-row">
          {/* -------- Legend Color Palete -------------- */}
          <div
            class="chart__legend--dot uui-dv-legend-label"
            style={{
              backgroundColor: this.model.getColorForDataNIndex(
                this.model.series[0].data[0],
                legendIndex
              )
            }}
          ></div>

          {/* -------- Legend Label ------------ */}
          <div class="chart__legend-label uui-dv-legend-label">
            {legendName}
          </div>
        </div>
      </Fragment>
    );
  }

  getTrendLineLegendItem(name: string): JSX.Element {
    return (
      <Fragment>
        <div class="chart__legend--flex-row">
          <svg
            width="12px"
            height="6px"
            class="uui-dv-legend-label"
            style={{ marginRight: '5px', marginTop: '2px' }}
          >
            <line
              x1="0"
              x2="12"
              y1="6"
              y2="6"
              style={{
                strokeWidth: '2px',
                stroke: 'var(--uui-color-dataviz-teal)'
              }}
            ></line>
          </svg>
          {/* -------- Legend Label ------------ */}
          <div class="chart__legend-label uui-dv-legend-label">{name}</div>
        </div>
      </Fragment>
    );
  }

  getLegendItems(legendItems: JSX.Element | JSX.Element[]): JSX.Element {
    return <div class="chart__legend--flex-centre">{legendItems}</div>;
  }

  getChartLegend(): JSX.Element {
    const legendItems = this.model.computedLengedLabels.map(
      (legendName, legendIndex) => {
        return this.getLegendItem(legendName, legendIndex);
      }
    );

    if (this.trendLineModel) {
      // trend line will only contain a single label
      const name = this.trendLineModel.computedLengedLabels[0];
      legendItems.push(this.getTrendLineLegendItem(name));
    }

    return <div> {this.getLegendItems(legendItems)} </div>;
  }

  updateNoBenchMarkTooltipPosition(x: number, y: number): void {
    const tooltipHeight =
      this.noBenchmarkToolTipElement?.getBoundingClientRect()?.height + 20;

    this.noBenchmarkToolTipElement.style.left = `${x - 15}px`;
    this.noBenchmarkToolTipElement.style.top = `${y - tooltipHeight}px`;
  }

  getNoBenchmarkTooltip(): JSX.Element {
    return (
      <div>
        <slot name="no-benchmark-tooltip" />
      </div>
    );
  }

  toogleTooltip(isShow: boolean): void {
    this.toolTipElement.style.opacity = isShow ? '1' : '0';
  }

  updateTooltipPosition(x: number, y: number): void {
    const documentWidth = document.body.offsetWidth;
    const rightSpace = documentWidth - x;
    const tooltipWidth =
      this.toolTipElement?.getBoundingClientRect()?.width + 20;

    if (rightSpace <= tooltipWidth) {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.style.left = `${x - tooltipWidth}px`;
    } else {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.style.left = `${x + 20}px`;
    }
    this.toolTipElement.style.top = `${y - 12}px`;
  }

  getIsolatedTooltipRow(
    bar: any,
    data: IChartData,
    barIndex: number
  ): JSX.Element {
    return (
      <div class="chart__legend--flex-row uui-dv-legend-label">
        {bar?.type === 'line' ? (
          <svg
            width="12px"
            height="6px"
            class="uui-dv-legend-label"
            style={{ marginRight: '7px', marginTop: '2px' }}
          >
            <line
              x1="0"
              x2="11"
              y1="6"
              y2="6"
              style={{
                strokeWidth: '2px',
                stroke: 'var(--uui-color-dataviz-teal)'
              }}
            ></line>
          </svg>
        ) : (
          <div
            class="chart__legend--dot uui-dv-legend-label"
            style={{
              backgroundColor: this.model.getColorForDataNIndex(data, barIndex)
            }}
          ></div>
        )}
        <div>{bar.barName}</div>
        <div class="chart__tooltip--value">
          {bar.displayLabelY || bar.barValue}
        </div>
        {this.model.tooltip.showBenchmarkDiff && (
          <div class="chart__tooltip--value">{bar.differenceWithBenchmark}</div>
        )}
      </div>
    );
  }

  getSingleTooltipRow(
    bars: IGroupBarTooltipDataItem[],
    data: IChartData
  ): JSX.Element {
    const barIndex = bars.findIndex(
      r => r.barName === data.x && r.dataValue === data.y
    );

    return (
      <div class="chart__legend--flex-column">
        <div class="chart__legend--row-container">
          <div class="chart__legend--flex-row uui-dv-legend-label">
            <div
              class="chart__legend--dot uui-dv-legend-label"
              style={{
                backgroundColor: this.model.getColorForDataNIndex(
                  data,
                  barIndex
                )
              }}
            ></div>
            <div class="chart__tooltip--detailed">
              <b>Name: </b>
              {bars[barIndex].barName}
            </div>
          </div>
          <div class="chart__legend--flex-row uui-dv-legend-label">
            <div class="chart__legend--dot uui-dv-legend-label"></div>
            <div class="chart__tooltip--detailed">
              <b>Value: </b>
              {bars[barIndex].displayLabelY || bars[barIndex].barValue}
            </div>
          </div>
          {this.model.tooltip.showPercentage && (
            <div class="chart__legend--flex-row uui-dv-legend-label">
              <div class="chart__legend--dot uui-dv-legend-label"></div>
              <div class="chart__tooltip--detailed">
                <b>Percentage: </b>
                <div>
                  {(this.model.isHorizontal
                    ? this.formatPercentage(
                        bars[barIndex].dataValue,
                        this.horizontalDataKeyVsDataAccumulatedValuePair.get(
                          data.name
                        )
                      )
                    : this.formatPercentage(
                        bars[barIndex].dataValue,
                        this.verticalDataKeyVsDataAccumulatedValuePair.get(
                          data.name
                        )
                      )) + '%'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  formatPercentage = (numerator: number, denomenator: number) => {
    const valueToDisplay =
      denomenator > 0 ? (numerator / denomenator) * 100 : 0;
    const percentageFormatter = d3.format('.1f');
    return percentageFormatter(valueToDisplay);
  };

  getTooltip(data: IChartData, highlightedGroup: string): JSX.Element {
    highlightedGroup = highlightedGroup || data.name;
    const tooltip = this.model.stackedBarTooltipData
      .find(g => g.name == highlightedGroup)
      .tooltipData.filter(f => !f.isHidden);

    if (this.trendLineModel) {
      const series = this.trendLineModel.series[0];
      const value = this.trendLineModel.series[0].data.find(
        s => s.name === data.name
      );

      const trendline: IGroupBarTooltipDataItem = {
        barName: `${series.name}`,
        barValue: `${series.valuePrefix ?? ''}${getFormattedNumberAbbrev(value.y, null, this.model.decimalPlaces)}`,
        dataValue: value.y,
        type: 'line'
      };

      tooltip.push(trendline);
    }
    return (
      <div class="chart__legend--flex-column">
        {this.model.tooltip.showTitle && (
          <div class="uui-dv-legend-title">{highlightedGroup}</div>
        )}
        <div class="chart__legend--row-container">
          {this._getTooltipRows(tooltip, data)}
        </div>
      </div>
    );
  }

  private _getTooltipRows(
    tooltip: IGroupBarTooltipDataItem[],
    data: IChartData
  ): JSX.Element[] {
    switch (this.model.tooltipBehavior) {
      case STACKED_BAR_CHART_TOOLTIP_BEHAVIOR.INDIVIDUAL:
        return [this.getSingleTooltipRow(tooltip, data)];
      case STACKED_BAR_CHART_TOOLTIP_BEHAVIOR.ISOLATED:
      default:
        return tooltip.map((bar, barIndex) =>
          this.getIsolatedTooltipRow(bar, data, barIndex)
        );
    }
  }

  //#region - Tooltip
  getChartTooltip(): JSX.Element {
    const rows = this.getTooltip(
      this.highlightedBarData,
      this.highlightedGroup
    );

    if (this.trendLineModel) {
    }

    return (
      <Fragment>
        <div class="chart__legend--flex-centre">{rows}</div>
      </Fragment>
    );
  }
  //#endregion

  render() {
    const chartSectionClass = this.model.isShowChart
      ? 'chart__wrapper'
      : 'chart--hidden';

    const viewBoxCoords =
      this.model.stretchToMaxWidth && this.width
        ? `0 0 ${this.width} ${this.model.height}`
        : '';

    return (
      <Host>
        {/*--------------- Title --------- */}
        {(this.model.title || this.model.subTitle) && (
          <uui-chart-title model={this.model}>
            {this.model.title?.useSlot && (
              <slot name="title" slot="title"></slot>
            )}
            {this.model.subTitle?.useSlot && (
              <slot name="sub-title" slot="sub-title"></slot>
            )}
          </uui-chart-title>
        )}

        <section class={chartSectionClass}>
          {/* ------- chart ---------- */}
          <svg
            class="chart__svg"
            {...(this.model.stretchToMaxWidth &&
              this.width && { viewBox: viewBoxCoords })}
            preserveAspectRatio="none"
          ></svg>

          {/* --------- legend ------------- */}
          {this.model.legend &&
            this.model.legend.showLegend &&
            this.getChartLegend()}
        </section>

        {/* ------------ Tooltip -------------- */}
        {this.model.tooltip && (
          <div
            class="chart__tooltip--container chart__tooltip--container-pointer-left"
            ref={el => (this.toolTipElement = el)}
          >
            {this.highlightedGroup != '' && this.getChartTooltip()}
          </div>
        )}

        {/* -----------  No Benchmark Tooltip -------------------*/}
        {this.model.enableCredibilityTooltip && (
          <div
            class="chart__tooltip--container chart__tooltip--container-pointer-bottom"
            ref={el => (this.noBenchmarkToolTipElement = el)}
          >
            {this.getNoBenchmarkTooltip()}
          </div>
        )}

        {!this.model.isShowChart && (
          <uui-chart-empty-message
            model={this.model}
            hasSlot={this.hasEmptyMessageSlot}
          >
            <slot name="empty-message" slot="empty-message"></slot>
          </uui-chart-empty-message>
        )}
      </Host>
    );
  }
}
